export interface TechStackOption {
  id: string;
  name: string;
  icon: string; // Now represents the tech name for Gilbarbara icons
  category: 'frontend' | 'backend' | 'database' | 'devops' | 'mobile' | 'other';
}

// Tech stack options with Gilbarbara-compatible names
export const techStackOptions: TechStackOption[] = [
  // Frontend
  { id: 'react', name: 'React', icon: 'react', category: 'frontend' },
  { id: 'vue', name: 'Vue.js', icon: 'vue', category: 'frontend' },
  { id: 'angular', name: 'Angular', icon: 'angular', category: 'frontend' },
  { id: 'svelte', name: 'Svelte', icon: 'svelte', category: 'frontend' },
  { id: 'nextjs', name: 'Next.js', icon: 'nextjs', category: 'frontend' },
  { id: 'typescript', name: 'TypeScript', icon: 'typescript', category: 'frontend' },
  { id: 'javascript', name: 'JavaScript', icon: 'javascript', category: 'frontend' },
  { id: 'tailwind', name: 'Tailwind CSS', icon: 'tailwindcss', category: 'frontend' },
  { id: 'sass', name: 'Sass', icon: 'sass', category: 'frontend' },
  { id: 'vite', name: 'Vite', icon: 'vite', category: 'frontend' },
  { id: 'webpack', name: 'Webpack', icon: 'webpack', category: 'frontend' },
  { id: 'framer-motion', name: 'Framer Motion', icon: 'framer', category: 'frontend' },
  
  // Backend
  { id: 'nodejs', name: 'Node.js', icon: 'nodejs', category: 'backend' },
  { id: 'python', name: 'Python', icon: 'python', category: 'backend' },
  { id: 'django', name: 'Django', icon: 'django', category: 'backend' },
  { id: 'flask', name: 'Flask', icon: 'flask', category: 'backend' },
  { id: 'express', name: 'Express', icon: 'express', category: 'backend' },
  { id: 'fastapi', name: 'FastAPI', icon: 'fastapi', category: 'backend' },
  { id: 'ruby', name: 'Ruby', icon: 'ruby', category: 'backend' },
  { id: 'rails', name: 'Rails', icon: 'rails', category: 'backend' },
  { id: 'php', name: 'PHP', icon: 'php', category: 'backend' },
  { id: 'laravel', name: 'Laravel', icon: 'laravel', category: 'backend' },
  { id: 'go', name: 'Go', icon: 'go', category: 'backend' },
  { id: 'rust', name: 'Rust', icon: 'rust', category: 'backend' },
  
  // Database
  { id: 'postgresql', name: 'PostgreSQL', icon: 'postgresql', category: 'database' },
  { id: 'mysql', name: 'MySQL', icon: 'mysql', category: 'database' },
  { id: 'mongodb', name: 'MongoDB', icon: 'mongodb', category: 'database' },
  { id: 'redis', name: 'Redis', icon: 'redis', category: 'database' },
  { id: 'sqlite', name: 'SQLite', icon: 'sqlite', category: 'database' },
  { id: 'supabase', name: 'Supabase', icon: 'supabase', category: 'database' },
  { id: 'firebase', name: 'Firebase', icon: 'firebase', category: 'database' },
  { id: 'prisma', name: 'Prisma', icon: 'prisma', category: 'database' },
  
  // DevOps & Cloud
  { id: 'docker', name: 'Docker', icon: 'docker', category: 'devops' },
  { id: 'kubernetes', name: 'Kubernetes', icon: 'kubernetes', category: 'devops' },
  { id: 'aws', name: 'AWS', icon: 'aws', category: 'devops' },
  { id: 'gcp', name: 'Google Cloud', icon: 'gcp', category: 'devops' },
  { id: 'azure', name: 'Azure', icon: 'azure', category: 'devops' },
  { id: 'vercel', name: 'Vercel', icon: 'vercel', category: 'devops' },
  { id: 'github-actions', name: 'GitHub Actions', icon: 'github', category: 'devops' },
  { id: 'gitlab', name: 'GitLab', icon: 'gitlab', category: 'devops' },
  
  // Mobile
  { id: 'react-native', name: 'React Native', icon: 'react', category: 'mobile' },
  { id: 'flutter', name: 'Flutter', icon: 'flutter', category: 'mobile' },
  { id: 'swift', name: 'Swift', icon: 'swift', category: 'mobile' },
  { id: 'kotlin', name: 'Kotlin', icon: 'kotlin', category: 'mobile' },
  { id: 'expo', name: 'Expo', icon: 'expo', category: 'mobile' },
  
  // Other
  { id: 'graphql', name: 'GraphQL', icon: 'graphql', category: 'other' },
  { id: 'rest-api', name: 'REST API', icon: 'rest-api', category: 'other' },
  { id: 'git', name: 'Git', icon: 'git', category: 'other' },
  { id: 'figma', name: 'Figma', icon: 'figma', category: 'other' },
  { id: 'stripe', name: 'Stripe', icon: 'stripe', category: 'other' },
  { id: 'openai', name: 'OpenAI', icon: 'openai', category: 'other' },
];

// Current app's tech stack (excluding Netlify)
export const currentAppTechStack = [
  'react',
  'typescript',
  'vite',
  'tailwind',
  'framer-motion',
  'supabase',
  'postgresql'
];

// Project categories with descriptions
export const projectCategories = [
  { 
    value: 'apps', 
    label: 'Apps',
    icon: 'lucide:globe',
    description: 'Web and mobile applications'
  },
  { 
    value: 'mobile app', 
    label: 'Mobile App',
    icon: 'lucide:smartphone',
    description: 'iOS, Android, or cross-platform mobile applications'
  },
  { 
    value: 'saas', 
    label: 'SaaS Product',
    icon: 'lucide:cloud',
    description: 'Software as a Service products and subscriptions'
  },
  { 
    value: 'game', 
    label: 'Game',
    icon: 'lucide:gamepad-2',
    description: 'Web or mobile games and interactive experiences'
  },
  { 
    value: 'ai ml', 
    label: 'AI/ML Project',
    icon: 'lucide:brain',
    description: 'Artificial Intelligence and Machine Learning projects'
  },
  { 
    value: 'api', 
    label: 'API/Backend',
    icon: 'lucide:server',
    description: 'Backend services, APIs, and microservices'
  },
  { 
    value: 'tool', 
    label: 'Developer Tool',
    icon: 'lucide:wrench',
    description: 'Tools, libraries, or utilities for developers'
  },
  { 
    value: 'ecommerce', 
    label: 'E-commerce',
    icon: 'lucide:shopping-cart',
    description: 'Online stores and marketplace platforms'
  },
  { 
    value: 'content', 
    label: 'Content Platform',
    icon: 'lucide:book-open',
    description: 'Blogs, media sites, or content management systems'
  },
  { 
    value: 'social', 
    label: 'Social Platform',
    icon: 'lucide:users',
    description: 'Social networks and community platforms'
  }
];
